import {Card, CardBody, Switch} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {useState} from "react";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import {config} from "../../../config/config.ts";
import Header from "../../../Header.tsx";
import services from "../../../services";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/races/')({
    component: RouteComponent,
})

function RouteComponent() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({title: '', distance: '', venue: '', starts_at: '', entry_fee: '', entry_currency: 'USD'});
    const [copied, setCopied] = useState<string | null>(null);

    const racesQuery = useQuery({
        queryKey: ['races'],
        queryFn:  () => services.races.list(),
    });

    // Needed so each race can say which season it counts towards — a race attached to nothing
    // silently contributes no points, which was easy to miss.
    const leaderboardsQuery = useQuery({
        queryKey: queryKeys.running.getRankings(),
        queryFn:  () => services.running.getRankings(),
    });
    const leaderboards = leaderboardsQuery.data || [];

    const setLeaderboard = async (race: any, uid: string) => {
        try {
            await services.races.update(race.uid, {ranking_group_uid: uid || null});
            refresh();
            queryClient.invalidateQueries({queryKey: queryKeys.running.getRankings()});
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not set the leaderboard.'});
        }
    };

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ['races']});
        queryClient.invalidateQueries({queryKey: ['races-dashboard']});
    };

    const createMut = useMutation({
        mutationFn: () => {
            const {entry_fee, entry_currency, ...rest} = form;
            const cents = Math.round(parseFloat(entry_fee || '0') * 100);
            return services.races.create({
                ...rest,
                status:            'published',
                registration_open: true,
                // Fee is entered in whole currency, stored in cents like every other event.
                ...(cents > 0 ? {entry_amount: cents, entry_currency_code: entry_currency} : {}),
            });
        },
        onSuccess:  () => {
            setForm({title: '', distance: '', venue: '', starts_at: '', entry_fee: '', entry_currency: 'USD'});
            setCreating(false);
            refresh();
            addToast({color: 'success', title: 'Race created — entries are open.'});
        },
        onError: (e: any) => addToast({color: 'danger', title: e?.message || 'Could not create the race.'}),
    });

    const toggleRegistration = async (race: any, open: boolean) => {
        try {
            await services.races.update(race.uid, {registration_open: open});
            refresh();
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not update the race.'});
        }
    };

    const remove = async (race: any) => {
        if (!window.confirm(`Delete "${race.title}" and its entries?`)) return;
        try {
            await services.races.remove(race.uid);
            refresh();
            addToast({color: 'success', title: 'Race deleted.'});
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not delete the race.'});
        }
    };

    const publicUrl = (race: any) => `${config.publicUrl}/race/${race.slug || race.uid}/`;

    const copyLink = async (race: any) => {
        const url = publicUrl(race);
        await navigator.clipboard.writeText(url);
        setCopied(race.uid);
        setTimeout(() => setCopied(null), 2000);
    };

    const races = racesQuery.data || [];

    return <>
        <Header backUrl={"/running/dashboard/"} />
        <Content>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-2xl font-bold">Races</h1>
                <PButton color="primary" onPress={() => setCreating(!creating)}
                         startContent={<Icon icon="material-symbols-light:add" className="text-xl" />}>
                    New race
                </PButton>
            </div>

            {creating && (
                <Card className="mb-4">
                    <CardBody className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <PInput label="Race name" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
                            <PInput label="Distance" placeholder="10K" value={form.distance} onChange={(e) => setForm({...form, distance: e.target.value})} />
                            <PInput label="Venue" value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} />
                            <PInput label="Date & time" type="datetime-local" value={form.starts_at} onChange={(e) => setForm({...form, starts_at: e.target.value})} />
                            <PInput label="Entry fee (0 = free)" type="number" placeholder="0.00"
                                    value={form.entry_fee} onChange={(e) => setForm({...form, entry_fee: e.target.value})} />
                            <div>
                                <label className="mb-1 block text-xs uppercase tracking-wide text-default-400">Currency</label>
                                <select value={form.entry_currency}
                                        onChange={(e) => setForm({...form, entry_currency: e.target.value})}
                                        className="w-full rounded-lg border border-default-200 bg-content1 p-2 text-sm">
                                    {['USD', 'GBP', 'EUR', 'AUD', 'CAD'].map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <PButton color="primary" isLoading={createMut.isPending}
                                     isDisabled={!form.title.trim()}
                                     onPress={() => createMut.mutate()}>Create race</PButton>
                            <PButton variant="light" onPress={() => setCreating(false)}>Cancel</PButton>
                        </div>
                    </CardBody>
                </Card>
            )}

            {racesQuery.isLoading ? (
                <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : races.length === 0 ? (
                <div className="py-12 text-center text-default-500">No races yet. Create one to start taking entries.</div>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {races.map((race: any) => (
                        <Card key={race.uid}>
                            <CardBody className="gap-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="truncate font-semibold">{race.title}</div>
                                        <div className="truncate text-xs text-default-500">
                                            {[race.distance, race.venue, race.starts_at ? new Date(race.starts_at).toLocaleDateString() : null]
                                                .filter(Boolean).join(' · ') || 'No details yet'}
                                        </div>
                                    </div>
                                    <span className="shrink-0 rounded-full bg-default-100 px-2 py-0.5 text-xs font-semibold capitalize">{race.status}</span>
                                </div>

                                <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div><span className="font-bold">{race.entries_count ?? 0}</span> <span className="text-default-500">entered</span></div>
                                    <div><span className="font-bold">{race.checked_in_count ?? 0}</span> <span className="text-default-500">checked in</span></div>
                                    <div><span className="font-bold">{race.finished_count ?? 0}</span> <span className="text-default-500">finished</span></div>
                                </div>

                                <Switch size="sm" isSelected={!!race.registration_open}
                                        onValueChange={(v) => toggleRegistration(race, v)}>
                                    <span className="text-sm">Entries open</span>
                                </Switch>

                                <div>
                                    <label className="mb-1 block text-xs uppercase tracking-wide text-default-400">
                                        Counts towards
                                    </label>
                                    <select
                                        value={race.ranking_group_uid || ''}
                                        onChange={(e) => setLeaderboard(race, e.target.value)}
                                        className="w-full rounded-lg border border-default-200 bg-content1 p-2 text-sm"
                                    >
                                        <option value="">No season leaderboard</option>
                                        {leaderboards.map((lb: any) => (
                                            <option key={lb.uid} value={lb.uid}>{lb.name}</option>
                                        ))}
                                    </select>
                                    {!race.ranking_group_uid && (
                                        <p className="mt-1 text-xs text-warning">
                                            {leaderboards.length === 0
                                                ? 'Create a leaderboard on the dashboard to build a season table.'
                                                : "This race won't add points to any season table."}
                                        </p>
                                    )}
                                </div>

                                {/* The sign-up link, shown rather than hidden behind "Share" — an
                                    organizer had no way to tell that entries go through the normal
                                    ScorePal registration flow, because nothing on this card led to it. */}
                                <div className="rounded-lg bg-default-50 p-2">
                                    <label className="mb-1 block text-xs uppercase tracking-wide text-default-400">
                                        Sign-up link
                                    </label>
                                    <a href={publicUrl(race)} target="_blank" rel="noreferrer"
                                       className="block truncate text-sm text-primary underline underline-offset-2">
                                        {publicUrl(race)}
                                    </a>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 border-t border-default-100 pt-3">
                                    <PButton className="flex-1" color="primary"
                                             onPress={() => window.open(publicUrl(race), '_blank', 'noopener')}
                                             startContent={<Icon icon="material-symbols-light:app-registration" className="text-xl" />}>
                                        Registration page
                                    </PButton>
                                    <PButton variant="bordered"
                                             onPress={() => navigate({to: `/running/races/${race.uid}`})}
                                             startContent={<Icon icon="material-symbols-light:how-to-reg-outline" className="text-xl" />}>
                                        Check-in
                                    </PButton>
                                    <PButton variant="bordered" onPress={() => copyLink(race)}
                                             startContent={<Icon icon={copied === race.uid ? 'material-symbols-light:check' : 'material-symbols-light:link'} className="text-xl" />}>
                                        {copied === race.uid ? 'Copied' : 'Share'}
                                    </PButton>
                                    <PButton color="danger" variant="bordered" isIconOnly onPress={() => remove(race)}>
                                        <Icon icon="material-symbols-light:delete-outline" className="text-xl" />
                                    </PButton>
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}
        </Content>
    </>
}
